/**
 * Les cles d'un cahier ne dans la console (spec du 03/10/2026, #271).
 *
 * La console calcule les cles d'un brouillon exactement comme le paquet `coloring` du PC :
 * l'agent reprend ces cles telles quelles a l'adoption, et refuse un dossier dont le nom ne
 * redonne pas la cle du cahier (`coloring.pousser.cle_du_dossier`). `ardoise` est donc celle de
 * `coloring.themes.ardoise` : NFKD, puis tout ce qui n'est pas ASCII disparait, minuscules, et
 * chaque suite de caracteres hors de [a-z0-9] devient un tiret. La fixture
 * `test/app-coloriage-cles.json`, lue aussi par `tests/test_themes.py`, prouve que les deux
 * disent la meme chose.
 *
 * Ecart connu, sans effet : les 36 lettres « outlined » d'Unicode 16 (U+1CCD6 a U+1CCF9)
 * deviennent de l'ASCII sous Node 22 et disparaissent sous Python 3.13 (Unicode 15.1), trouve
 * par un differentiel exhaustif le 3 octobre 2026. Le PC reprend les cles de la console telles
 * quelles, il ne les recalcule jamais : l'ecart ne touche aucun dossier.
 *
 * Ce fichier porte aussi ce que la console doit respecter pour que l'adoption par le PC ne
 * puisse pas echouer sur le contenu (relecture de #271) : `rogner` comme Python, les bornes du
 * titre et du sous-titre (page de titre), et celle d'une cle de page. Et ce qui lit une phrase
 * de variante deja en base comme le PC la lira (`rognerCommePython`,
 * `phraseEnregistreeUtilisable`, #293).
 */

/**
 * Rogne comme `str.strip()` de Python : les blancs de JavaScript, plus U+001C a U+001F et
 * U+0085, que Python tient pour des blancs et JavaScript non. Sans eux, un sujet fait du seul
 * U+0085 passait la console et faisait echouer l'adoption (`preparer_cahier` le voit vide).
 * U+FEFF, que JavaScript ote et Python garde, rend la console plus stricte, jamais moins.
 *
 * Un parcours des points de code plutot qu'une expression reguliere : Biome refuse, a raison,
 * les caracteres de controle dans une expression (`noControlCharactersInRegex`).
 */
export function rogner(texte: string): string {
  return rognerSi(texte, estUnBlanc);
}

/**
 * Rogne exactement comme `str.strip()` de Python : `rogner` sans U+FEFF, que Python garde. Pour
 * lire un texte deja en base comme le PC le lira (#293) ; l'entree, elle, rogne par `rogner`.
 * Une phrase enregistree avant la regle peut tenir U+FEFF entre deux U+0085 : `trim` gardait
 * U+0085, et le PC rogne les U+0085 mais garde le U+FEFF.
 */
export function rognerCommePython(texte: string): string {
  return rognerSi(
    texte,
    (caractere) => caractere.codePointAt(0) !== 0xfeff && estUnBlanc(caractere),
  );
}

function rognerSi(texte: string, blanc: (caractere: string) => boolean): string {
  const caracteres = Array.from(texte);
  let debut = 0;
  let fin = caracteres.length;
  while (debut < fin && blanc(caracteres[debut] ?? "")) debut += 1;
  while (fin > debut && blanc(caracteres[fin - 1] ?? "")) fin -= 1;
  return caracteres.slice(debut, fin).join("");
}

/** Les blancs de Python que JavaScript ne tient pas pour tels : U+001C a U+001F et U+0085. */
const BLANCS_DE_PYTHON_SEULEMENT: ReadonlySet<number> = new Set([0x1c, 0x1d, 0x1e, 0x1f, 0x85]);

/** Un blanc pour JavaScript ou pour Python : ceux de `str.isspace`, plus U+FEFF. */
function estUnBlanc(caractere: string): boolean {
  return /^\s$/.test(caractere) || BLANCS_DE_PYTHON_SEULEMENT.has(caractere.codePointAt(0) ?? -1);
}

/**
 * Vrai si un sujet donne un prompt : au moins une lettre ou un chiffre ASCII. `prompt_klein`
 * (`coloring/prompts.py`) refuse un sujet fait de blancs et de « .,;:!? » (« sujet vide ») :
 * un sujet `?` passait la console, lancait le rendu, puis faisait echouer la demande a chaque
 * relance, sommaire fige (contre-relecture de #271). Ce predicat est plus strict que le Python,
 * jamais moins : une lettre ASCII n'est ni un blanc ni de la ponctuation pour lui, et le sujet
 * s'ecrit en anglais. Le SQL du module dit la meme chose (`SANS_SUJET`, magasin.ts) ; la
 * fixture commune `app-coloriage-cles.json` epingle les trois.
 */
export function sujetUtilisable(sujet: string): boolean {
  return /[A-Za-z0-9]/.test(sujet);
}

/**
 * Vrai si une phrase de variante deja en base (ouverture, style, rendu) peut partir au PC telle
 * qu'elle est (#293). Ecrite avant la regle, elle n'a pas passe `phraseVarianteSchema` : on la
 * juge comme le PC la lira. Null, ou faite de blancs pour `str.strip`, il prend celle de la
 * reference (`_ecrite`), comme pour un champ vide ; sinon il l'emploie, et elle doit avoir une
 * lettre ou un chiffre ASCII, faute de quoi ce n'est pas une phrase en anglais. Sa longueur ne
 * compte pas : le PC n'a pas de borne.
 */
export function phraseEnregistreeUtilisable(phrase: string | null): boolean {
  if (phrase === null) return true;
  const lue = rognerCommePython(phrase);
  return lue === "" || sujetUtilisable(lue);
}

/**
 * Le titre et le sous-titre d'un cahier ne dans la console : 60 caracteres au plus. Mesure du
 * 3 octobre 2026 sur `coloring.montage.page_de_titre`, deux formats : un mot d'un seul tenant
 * fait de W passait jusqu'a 64 caracteres, mais une quarantaine de glyphes plus larges que W
 * (`@`, les ligatures larges) cassaient avant 60 (contre-relecture de #271). Depuis, la page
 * de titre coupe a la taille minimale un mot plus large que la ligne : 60 caracteres de chacun
 * des glyphes larges essayes tiennent dans les deux formats (`tests/test_montage.py`, sur les
 * bornes de la fixture). Reste un cas connu, en sous-titre seulement : trois mots de glyphes
 * tres larges dont celui du milieu ne partage sa ligne avec aucun voisin demandent trois
 * lignes pour deux ; l'agent le dit a l'adoption, avant d'ecrire quoi que ce soit.
 */
export const TITRE_CAHIER_MAX = 60;
export const SOUS_TITRE_CAHIER_MAX = 60;

/** La cle d'une page, comme `clePageSchema` du contrat : 160 caracteres au plus. */
export const CLE_PAGE_MAX = 160;

/** Le nom sobre d'un titre francais, comme `coloring.themes.ardoise` ; `page` s'il ne reste rien. */
export function ardoise(texte: string): string {
  // Par point de code, comme `encode("ascii", "ignore")` : un emoji part en entier.
  const ascii = Array.from(texte.normalize("NFKD"))
    .filter((caractere) => (caractere.codePointAt(0) ?? 128) < 128)
    .join("");
  const cle = ascii
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return cle === "" ? "page" : cle;
}

/**
 * La cle d'une page, comme `coloring.livre.fabriquer` : le rang sur deux chiffres au moins
 * (`f"{rang:02d}"`), puis l'ardoise du titre. Deux pages ne partagent jamais une cle : le
 * rang la commence.
 */
export function clePage(rang: number, titre: string): string {
  return `${String(rang).padStart(2, "0")}-${ardoise(titre)}`;
}

/** Les noms que Windows refuse pour un dossier, en minuscules. */
export const NOMS_RESERVES: ReadonlySet<string> = new Set([
  "con",
  "prn",
  "aux",
  "nul",
  ...Array.from({ length: 9 }, (_, i) => `com${i + 1}`),
  ...Array.from({ length: 9 }, (_, i) => `lpt${i + 1}`),
]);

/** La longueur d'une cle de cahier, comme `coloring.pousser.LONGUEUR_CLE`. */
export const LONGUEUR_CLE = 120;

/**
 * Vrai si `cle` est deja ce qu'`ardoise` rend : minuscules et chiffres separes par des tirets
 * simples, ni tiret en tete ni en fin, 120 caracteres au plus, et pas un nom reserve de
 * Windows. Une autre cle donnerait un dossier dont `cle_du_dossier` ne redonne pas la cle.
 */
export function cleNormale(cle: string): boolean {
  return (
    /^[a-z0-9]+(-[a-z0-9]+)*$/.test(cle) && cle.length <= LONGUEUR_CLE && !NOMS_RESERVES.has(cle)
  );
}

/**
 * La cle proposee depuis un titre : son ardoise, coupee a 120 caracteres, sans tiret final ;
 * un nom reserve de Windows prend `-cahier` derriere lui. Toujours `cleNormale`.
 */
export function cleDeTitre(titre: string): string {
  const cle = ardoise(titre).slice(0, LONGUEUR_CLE).replace(/-+$/, "");
  return NOMS_RESERVES.has(cle) ? `${cle}-cahier` : cle;
}
